import {ChevronDownIcon, ClockIcon, CubeIcon, ReceiptPercentIcon, TagIcon, TruckIcon} from "@heroicons/react/20/solid";
import type {TicketCostBucketKey, TicketCostLine} from "./ticket-cost-types";
import {formatTicketCostCurrency} from "./ticket-cost-types";

export type TicketCostComponentBreakdownProps = {
  lines: TicketCostLine[];
  currency?: string;
  defaultOpen?: boolean;
};

export const TicketCostBucketIcon = ({bucket, className = "size-4"}: {bucket: TicketCostBucketKey; className?: string}) => {
  if (bucket === "time") return <ClockIcon className={className}/>;
  if (bucket === "freight") return <TruckIcon className={className}/>;
  if (bucket === "load") return <CubeIcon className={className}/>;
  if (bucket === "tax") return <ReceiptPercentIcon className={className}/>;
  return <TagIcon className={className}/>;
};

/** Icon-led totals by database cost-component type without repeating ledger details. */
export const TicketCostComponentBreakdown = ({lines, currency = "USD", defaultOpen = false}: TicketCostComponentBreakdownProps) => {
  const components = Array.from(lines.reduce((result, line) => {
    const key = line.component?.code ?? "UNCLASSIFIED";
    const current = result.get(key) ?? {code: key, label: line.component?.label ?? "Unclassified", bucket: line.bucket, amount: 0};
    current.amount += line.amount;
    result.set(key, current);
    return result;
  }, new Map<string, {code: string; label: string; bucket: TicketCostLine["bucket"]; amount: number}>()).values()).sort((a, b) => b.amount - a.amount);

  return (
    <details open={defaultOpen} className="group/component border-t border-[var(--ots-outline-variant)]">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-2 text-xs font-semibold text-[var(--ots-secondary)] marker:content-none focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-[var(--ots-primary)]">
        <span>Component breakdown <span className="font-normal text-[var(--ots-text-muted)]">({components.length})</span></span>
        <ChevronDownIcon className="size-3.5 text-[var(--ots-text-muted)] transition-transform group-open/component:rotate-180"/>
      </summary>
      <div className="grid gap-x-6 gap-y-1 border-t border-[var(--ots-outline-variant)] bg-[var(--ots-background)] px-4 py-2.5 sm:grid-cols-2 lg:grid-cols-3">
        {components.map((component) => (
          <div key={component.code} className="flex min-w-0 items-center justify-between gap-3 py-1 text-xs">
            <span className="flex min-w-0 items-center gap-2">
              <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-[var(--ots-surface-variant)] text-[var(--ots-text-muted)]" aria-hidden="true">
                <TicketCostBucketIcon bucket={component.bucket}/>
              </span>
              <span className="truncate font-medium text-[var(--ots-text)]">{component.label}</span>
            </span>
            <strong className="shrink-0 tabular-nums text-[var(--ots-secondary)]">{formatTicketCostCurrency(component.amount, currency)}</strong>
          </div>
        ))}
      </div>
    </details>
  );
};
